/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { classNames } from '../utils/UNSAFE_classNames';
import { dimensionInterpolations } from '../utils/UNSAFE_interpolations/dimensions';
import type { DimensionProps } from '../utils/UNSAFE_interpolations/dimensions';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { borderInterpolations } from '../utils/UNSAFE_interpolations/borders';
import type { BorderProps } from '../utils/UNSAFE_interpolations/borders';
import { stringLiteralArray } from '../utils/UNSAFE_arrayUtils';
import { WithRequired } from '../utils/UNSAFE_typeUtils';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { SkeletonRedwoodTheme } from './themes/redwood/SkeletonTheme';

const dimensions = stringLiteralArray(['height', 'width']);
type Dimension = typeof dimensions[number];
const border = stringLiteralArray(['borderRadius']);
type BorderRadius = typeof border[number];

// This component has some StyleInterpolationProps. Those are the height and width properties of the SkeletonProps.
type StyleInterpolationProps = Pick<DimensionProps, Dimension>;
// This component has some BorderRadiusProps, which is the borderRadius property of the SkeletonProps.
type BorderRadiusProps = Pick<BorderProps, BorderRadius>;
//This type has StyleInterpolationProps and BorderRadiusProps with the height prop made required.
type SkeletonProps = WithRequired<StyleInterpolationProps, 'height'> & BorderRadiusProps;

// Create an array [dimensionInterpolations['height'], dimensionInterpolations['width']]
const skeletonDimensionInterpolation = Array.from(dimensions, (x) => dimensionInterpolations[x]);
const borderRadiusInterpolation = Array.from(border, (x) => borderInterpolations[x]);
const interpolations = [
  ...Object.values(borderRadiusInterpolation),
  ...Object.values(skeletonDimensionInterpolation)
];

const SkeletonInterpolations = mergeInterpolations<SkeletonProps>(interpolations);
/**
 * Skeleton component allows the appropriate skeleton to be rendered based on the
 * property values
 **/
export function Skeleton({ ...props }: SkeletonProps) {
  const { classes: skeletonBase } = useComponentTheme(SkeletonRedwoodTheme);
  const classes = classNames([skeletonBase]);

  const skeletonDimensions = SkeletonInterpolations({ width: '100%', borderRadius: 0, ...props });
  return <div style={skeletonDimensions} class={classes} />;
}
